import React, { useState } from "react";
import InputLabel from "@mui/material/InputLabel";
import CardMedia from "@mui/material/CardMedia";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ClickAwayListener from "@mui/material/ClickAwayListener";
// import DtvTheme from "@directv-digital/dds.ui.dtv-theme";

export interface SelectItem {
  [key: string]: {
    label: string;
    value: string;
  }
}
export interface SelectProps {
  id?: string;
  label?: string;
  dynamicLabel?: string;
  onChange?: (option: SelectItem) => void;
  options?: any[];
  value?: any;
  prefix?: string;
  isDark?: boolean;
  errorText?: string;
  handleDropDownClick?: () => void;
}

const spaceBarIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.keyCode === 32;
const enterIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.keyCode === 13;
const upArrowIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.keyCode === 38;
const downArrowIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.keyCode === 40;
const escIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.keyCode === 27;

export function SelectDropdown(props: SelectProps) {
  try {
    const dropdownLabelRef = React.useRef<HTMLInputElement>();
    const dropdownListRef = React.useRef<HTMLDivElement>(null);

    const optionsObject = props?.options?.reduce((acc, option) => {
      acc[option.key] = option.label;
      return acc;
    }, {});
    const [ddOpen, setDDOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(props?.value);

    const toggleSortingDropdown = () => setDDOpen(!ddOpen);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      const target = e.target as Element;
      const labelRef = dropdownLabelRef?.current;
      const listRef = dropdownListRef?.current;
      const gotoFirst = () => {
        (listRef?.querySelector("li") as HTMLElement)?.focus();
      }
      const goToLast = () => {
        const lastItem = listRef?.querySelectorAll("li");
        (lastItem[lastItem.length - 1] as HTMLElement)?.focus()
      }
      const goToLabel = () => {
        labelRef?.focus();
      }
      if (escIsPressed(e)) {
        toggleSortingDropdown();
        return;
      }

      if (labelRef?.contains(target)) {
        if (enterIsPressed(e) || spaceBarIsPressed(e)) {
          setSelectedValue(target.getAttribute("data-value"));
          toggleSortingDropdown();
          goToLabel();
          return;
        }
        if (downArrowIsPressed(e)) {
          gotoFirst();
        }
        if (upArrowIsPressed(e)) {
          goToLast();
        }
        e.preventDefault();
      } else if (listRef?.contains(target)) {
        if (downArrowIsPressed(e)) {
          if (target.nextElementSibling) {
            (target.nextElementSibling as HTMLElement).focus();
          } else {
            goToLabel();
          }
          e.preventDefault();
        } else if (upArrowIsPressed(e)) {
          if (target.previousElementSibling) {
            (target.previousElementSibling as HTMLElement).focus();
          } else {
            goToLabel();
          }
          e.preventDefault();
        } else if (enterIsPressed(e) || spaceBarIsPressed(e)) {
          const dataValue = target.getAttribute("data-value");
          if (dataValue) {
            setSelectedValue(dataValue);
            toggleSortingDropdown();
            // props.onChange(e, true, null);
            goToLabel();
          }
        }
      }
    };

    const onChange = (event, option) => {
      console.log(event, option)
      const target = event.target as Element;
      setSelectedValue(target.getAttribute("data-value"));
      props.onChange(option);
      if (dropdownLabelRef?.current) {
        dropdownLabelRef.current.focus();
      }
      setDDOpen(false);
    };

    const handlePairwiseBlur = e => {
      console.log(e)
      const target = e?.target;
      const relatedTarget = e?.relatedTarget || document?.activeElement;

      if (
        (dropdownListRef?.current && dropdownListRef?.current?.contains(target)) ||
        (dropdownLabelRef?.current && dropdownLabelRef?.current?.contains(target)) ||
        e?.currentTarget?.contains?.(relatedTarget)
      ) {
        return;
      }
      setDDOpen(false);
    }

    const commonBoxStyles = {
      backgroundColor: "#ffffff !important",
      width: "100% !important",
      textOverflow: "ellipsis",
      overflow: "hidden",
      textWrap: "nowrap",
      boxSizing: "border-box",
      cursor: "pointer",
      fontSize: "16px"
    }

    const onClickAway = () => {
      setDDOpen(false)
    }

    return (

        <>
            {props?.label &&
              <InputLabel sx={{
                color: "#333333",
                fontSize: "16px",
                fontWeight: 400,
                margin: "0px",
                zIndex: 120
              }}>
                {props?.label}
              </InputLabel>
            }
            <ClickAwayListener onClickAway={onClickAway}>
                <DtvBox>
                  <ListItemButton
                    data-testid="listBox"
                    data-value={selectedValue} id={`SelectLabel-${props?.id}`}
                    ref={dropdownLabelRef}
                    role="listbox"
                    tabIndex={0}
                    aria-activedescendant={
                      selectedValue && props?.options?.some(opt => `listbox-${opt.value}` === `listbox-${selectedValue}`) ?
                        `listbox-${selectedValue}` :
                        undefined
                    }
                    sx={{
                      ...commonBoxStyles,
                      transition: "background-color 200ms ease",
                      lineHeight: "24px",
                      margin: "0px !important",
                      fontWeight: 400,
                      height: {
                        xs: "48px",
                        md: "56px"
                      },
                      outline: "0 none",
                      position: "relative",
                      zIndex: 5000,
                      display: "flex",
                      padding: "0px 0px 0px 15px !important",
                      border: "1px solid #8D8D8D",
                      borderRadius: "4px",
                      "&:hover": {
                        "& div": {
                          backgroundColor: "#F2F2F2",
                          color: "#1D2329",
                          borderRadius: "4px"
                        },
                        "&:focus": {
                          "& div": {
                            textDecoration: "underline"
                          }
                        }
                      }
                    }}
                    onBlur={handlePairwiseBlur}
                    onClick={toggleSortingDropdown}
                    onKeyDown={handleKeyDown}
                  >
                    <ListItemText
                      aria-label={`${props?.prefix ? `${props.prefix} ` : ''}${optionsObject[`${props?.value}`] || ''} selected`}
                      primary={!selectedValue && props?.dynamicLabel ?
                        props?.dynamicLabel :
                        `${props?.prefix ? `${props.prefix} ` : ''}${optionsObject[`${props?.value}`] || ''}`
                      }
                      sx={{
                        padding: "5px 3px",
                        marginLeft: "-3px",
                        flex: "unset",
                        fontSize: "16px",
                        fontWeight: 400,
                        margin: "0px",
                        color: props?.isDark ? "#ffffff" : "#333333",
                        transition: "background-color 100ms ease, text-decoration 100ms ease"
                      }}
                    />
                        <CardMedia
                          alt={`Click to ${ddOpen ? 'close' : 'open'} dropdown`}
                          component="img"
                          src="https://www.directv.com/dtvassets/global/icons/2022/arrow-down_lt-bg.svg"
                          sx={{
                            height: "24px",
                            width: "24px",
                            color: "#000000",
                            display: "inline-block",
                            margin: "0px",
                            outline: "0",
                            paddingTop: "0px",
                            position: 'absolute',
                            right: "0px",
                            top: {
                              xs: "12px",
                              md: "16px"
                            },
                            marginRight: "14px",
                            transform: !ddOpen ? "rotate(0deg)" : "rotate(180deg)",
                            transition: "transform 200ms cubic-bezier(0.42, 0, 0.58, 1) 150ms"
                          }}
                        />
                  </ListItemButton>

                  <DtvBox
                    id={`SelectList-${props?.id}`}
                    ref={dropdownListRef}
                    onKeyDown={handleKeyDown}
                    // Generated by Copilot- Start - 2026-03-27 16:31:20
                    sx={{
                      width: "100%",
                      position: "absolute",
                      top: {
 xs: "54px",
lg: "62px"
},
                      backgroundColor: "#ffffff !important",
                      color: "#102641",
                      display: "flex",
                      flexDirection: "column",
                      margin: "0px",
                      outline: "0 none",
                      borderRadius: "4px",
                      ...(ddOpen ? {
                        border: '1px solid #8D8D8D',
                        maxHeight: "300px"
                      } : {
                        border: 'none',
                        pointerEvents: "none",
                        maxHeight: "0px"
                      }),
                      transition: "height 150ms cubic-bezier(0.42, 0, 0.58, 1), padding 75ms cubic-bezier(0.42, 0, 0.58, 1)"
                    }}
                    // Generated by Copilot- End - 2026-03-27 16:31:20
                  >
                    {props?.options && props?.options.map((option, index) => (
                      <ListItemButton
                        aria-label={option.label}
                        aria-selected={props?.value === option?.value ? "true" : "false"}
                        component="li"
                        data-testid="listItem"
                        data-value={option.value}
                        id={option.value}
                        key={index}
                        role="option"
                        tabIndex={0}
                        value={option.value}
                        sx={{
                          ...commonBoxStyles,
                          width: "100% !important",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          textWrap: "nowrap",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          fontSize: "16px",
                          lineHeight: "24px",
                          display: 'block',
                          padding: ddOpen ? "10px 16px !important" : "0px",
                          "&:hover": {
                            "& div": {
                              backgroundColor: "#F2F2F2",
                              color: "#1D2329",
                              borderRadius: "4px"
                            },
                            "&:focus": {
                              "& div": {
                                textDecoration: "underline"
                              }
                            }
                          }
                        }}
                        onClick={e => onChange(e, option)}
                      >
                        <ListItemText
                          primary={option.label}
                          sx={{
                            flex: "unset",
                            padding: "5px 3px",
                            marginLeft: "-3px",
                            borderRadius: "4px",
                            margin: 0,
                            transition: "background-color 100ms ease, text-decoration 100ms ease"
                          }}
                        />
                      </ListItemButton>
                      ))}
                  </DtvBox>
              </DtvBox>
            </ClickAwayListener>
            </>

    );
  } catch (_error) {
    return <></>;
  }
}
